import { Button, Col, Form, Input, InputNumber, Modal, Row } from "antd";
import FormItem from "antd/es/form/FormItem";
import { useEffect, useState } from "react";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import { AreaProps } from "src/entity/company/company-response.entity";
import { ChemicalVolume, ManualChemicalVolume, StaticColumnsType } from "src/entity/terminal-marketing/terminal-marketing.entity";
import { ReportStatusEnum } from "src/entity/voc-approve/voc-approve.entity";
import { onKeyDownNumberWithFixedDecimal } from "src/utils/key-down-validate";
import { formatNumber } from "src/utils/process-number";

const ManualChemicalVolumeModal = (props: {
  open: boolean;
  data: ChemicalVolume;
  loading: boolean;
  areaMaster: AreaProps[];
  onCloseModal: () => void;
  onSave: (data: ChemicalVolume) => void;
}) => {
  const { open, data, loading, areaMaster, onCloseModal, onSave } = props;
  const [form] = Form.useForm();
  const [dataChange, setDataChange] = useState<ChemicalVolume>(data);

  type MonthManual = keyof ManualChemicalVolume;
  const months: MonthManual[] = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

  // Update dataChange when data prop changes
  useEffect(() => {
    setDataChange(data);
  }, [data]);

  const handleSave = async () => {
    try {
      onSave(dataChange);
    } catch (error) {
      console.error(error);
    }
  };

  const getMasterLabel = (type: string, text: string) => {
    if (!text || typeof text != "string") return "";
    if (type === "area" && areaMaster) {
      const optValue = areaMaster.find((item) => item._id === text);
      return optValue?.areaName;
    } else {
      return text;
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      ...data,
      area: getMasterLabel("area", data.area),
    });
  }, [data]);

  const staticColumnsKey: StaticColumnsType[] = [
    {
      label: "Chemical Name",
      key: "chemicalName",
    },
    {
      label: "Area",
      key: "area",
    },
    {
      label: "Loading By",
      key: "loadingBy",
    },
    {
      label: "Unit",
      key: ["manualChemicalVolume", "unit"],
    },
  ];

  const handleValuesChange = (changedValues: any, allValues: any) => {
    if (changedValues.manualChemicalVolume) {
      const manualChemicalVolume = changedValues.manualChemicalVolume;
      Object.keys(manualChemicalVolume).forEach((key) => {
        if (!manualChemicalVolume[key].value) {
          changedValues.manualChemicalVolume[key].value = 0;
        }
      });
      form.setFieldsValue({
        manualChemicalVolume: changedValues.manualChemicalVolume,
      });
    }
  };

  const handleChangeData = (value: number | null, month: keyof ManualChemicalVolume) => {
    setDataChange((prevData) => ({
      ...prevData,
      manualChemicalVolume: {
        ...prevData.manualChemicalVolume,
        [month]: {
          ...prevData.manualChemicalVolume[month],
          value: value,
        },
      },
    }));
  };

  return (
    <div>
      <Modal
        title={<ModalTitle title="Chemical Volume" />}
        open={open}
        width={1000}
        okText="Save"
        onCancel={onCloseModal}
        centered
        bodyStyle={{ height: 300, overflowY: "auto", overflowX: "hidden" }}
        maskClosable={false}
        footer={
          <Row className="w-full border-t" justify={"end"}>
            <Button className="mt-4" key="submit" type="primary" onClick={handleSave} loading={loading}>
              Save
            </Button>
          </Row>
        }
      >
        <Form size="large" layout="vertical" autoComplete="off" form={form} onValuesChange={handleValuesChange}>
          <Row gutter={16}>
            {staticColumnsKey.map((column, index) => (
              <Col xs={{ span: 24 }} sm={{ span: 6 }} md={{ span: 6 }} key={index}>
                <FormItem label={column.label} name={column.key}>
                  <Input disabled />
                </FormItem>
              </Col>
            ))}
          </Row>
          <Row gutter={16}>
            {months.map((month, index) => (
              <Col xs={{ span: 24 }} sm={{ span: 6 }} md={{ span: 4 }} key={index}>
                <Form.Item label={month.charAt(0).toUpperCase() + month.slice(1)} name={["manualChemicalVolume", month, "value"]}>
                  <InputNumber
                    min={0}
                    step={1}
                    style={{ width: "100%" }}
                    disabled={data.manualChemicalVolume[month as keyof ManualChemicalVolume].reportStatus !== ReportStatusEnum.PROCESSING}
                    onKeyDown={(e) => {
                      onKeyDownNumberWithFixedDecimal(e, 2);
                    }}
                    onChange={(value) => handleChangeData(value, month)}
                    formatter={(value) => formatNumber(value, 2)}
                  />
                </Form.Item>
              </Col>
            ))}
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default ManualChemicalVolumeModal;
